import {
  all,
  call,
  put,
  race,
  select,
  take,
  takeEvery,
} from 'redux-saga/effects';
import { requestSuccess as watchSuccess } from '@wix/da-shared-react/pkg/WatchButton/redux/actionCreators';
import { toggleWatchGroup } from '@wix/da-shared-react/pkg/NetworkBar/redux/actions';
import { request } from '@wix/da-shared-react/pkg/xhr/request';
import { getThisGroup } from '../selectors/page';
import {
  groupJoinFail,
  groupJoinPending,
  groupJoinSuccess,
  groupLeaveFail,
  groupLeaveSuccess,
  requestJoinGroup,
  requestLeaveGroup,
} from '../actions/groups';
import {
  PapiDaSharedGroupJoin,
  PapiDaSharedGroupLeave,
  PapiRequestDaSharedGroupJoin,
  PapiRequestDaSharedGroupLeave,
  PapiUser,
} from '@wix/da-types-papi';
import { addNotification } from '@wix/da-shared-react/pkg/Notifications/redux/actionCreators';
import { NotificationType } from '@wix/da-shared-react/pkg/Notifications/redux/types';
import {
  confirmNo,
  confirmYes,
  pushGlobalConfirmationModal,
  pushModal,
} from '@wix/da-shared-react/pkg/Modals/redux/actionCreators';
import { getUserWatchState } from '@wix/da-shared-react/pkg/WatchButton/redux/selectors';
import { ModalType } from '../types/notification';
import i18next from 'i18next';
import { userSchema } from '@wix/da-shared-react/pkg/redux/normalizr/schemas/user';
import { normalize } from '@wix/da-shared-react/pkg/redux/normalizr';
import { putEntities } from '@wix/da-shared-react/pkg/redux/entities/actions';

// TODO: see if we can move all these to shared...
function* handleWatch({
  payload: { username, isWatching },
}: ReturnType<typeof watchSuccess>) {
  const thisGroup: PapiUser | undefined = yield select(getThisGroup);
  if (username === thisGroup?.username) {
    yield put(toggleWatchGroup(thisGroup.userId, isWatching));
  }
}

function* handleClosedGroupJoinRequest(group: PapiUser) {
  const groupWatchState: ReturnType<typeof getUserWatchState> = yield select(
    getUserWatchState,
    group
  );

  if (groupWatchState.isWatching) {
    yield put(
      addNotification(NotificationType.Generic, {
        message: i18next.t('groupFeed.errors.join.closed', {
          groupname: group.username,
        }),
        isError: true,
        cardProps: {
          multiLine: true,
        },
      })
    );

    return;
  }

  yield put(
    pushModal(ModalType.GROUP_CLOSED_WATCH, {
      params: { group },
      wrapInPopup: false,
    })
  );
}

function* handleGroupJoinRequest({
  payload: { group },
}: ReturnType<typeof requestJoinGroup>) {
  if (!group.extendedGroup?.apps?.join?.canJoin) {
    yield call(handleClosedGroupJoinRequest, group);
    return;
  }

  const roleId = group.extendedGroup?.apps?.join.intoRoleid;
  if (!roleId) {
    return;
  }
  const data: PapiRequestDaSharedGroupJoin = {
    groupid: group.userId,
    roleid: roleId,
  };
  const result: PapiDaSharedGroupJoin | false = yield call(request, {
    method: 'post',
    url: 'group/join',
    data,
  });

  if (!result || !result.success || !result.thisGroup) {
    yield all([
      put(
        addNotification(NotificationType.Generic, {
          message: result && result.errorDescription,
          messageLocaleKey: result
            ? `groupFeed.errors.join.${result.errorDescription}`
            : 'groupFeed.errors.join.generic',
          isError: true,
          cardProps: {
            multiLine: true,
          },
        })
      ),
      put(
        groupJoinFail(
          group,
          (result && result.errorDescription) ||
            i18next.t('groupFeed.errors.join.generic')
        )
      ),
    ]);
    return;
  }

  const { entities } = normalize(result.thisGroup, userSchema);
  yield put(putEntities({ entities }));

  if (result.thisGroup.extendedGroup?.isIngroup) {
    yield put(groupJoinSuccess(group));
    return;
  }

  yield all([
    put(groupJoinPending(group)),
    put(
      pushModal(ModalType.GROUP_JOIN_PENDING, {
        params: { group },
        wrapInPopup: false,
      })
    ),
  ]);
}

function* handleGroupLeaveRequest({
  payload: { group },
}: ReturnType<typeof requestLeaveGroup>) {
  if (!group.extendedGroup?.apps?.join.canLeave) {
    pushGlobalConfirmationModal({
      confirmBtnLabelTransKey: 'group.leave.founder.button.accept',
      textTransKey: 'group.leave.founder.body',
      titleTransKey: 'group.leave.founder.heading',
      confirmBtnType: 'danger',
      translationOptions: { groupname: group.username },
      withCancelButton: false,
    });
    return;
  }

  yield put(
    pushGlobalConfirmationModal({
      cancelBtnLabelTransKey: 'common.cancel',
      confirmBtnLabelTransKey: 'group.leave.confirm.button.leave',
      textTransKey: 'group.leave.confirm.body',
      titleTransKey: 'group.leave.confirm.heading',
      confirmBtnType: 'danger',
      translationOptions: { groupname: group.username },
    })
  );

  const { no } = yield race({
    yes: take(confirmYes),
    no: take(confirmNo),
  });

  if (no) {
    return;
  }

  const data: PapiRequestDaSharedGroupLeave = {
    groupid: group.userId,
  };
  const result: PapiDaSharedGroupLeave | false = yield call(request, {
    method: 'post',
    url: 'group/leave',
    data,
  });

  if (!result || !result.success) {
    yield all([
      put(
        addNotification(NotificationType.Generic, {
          message: result && result.errorDescription,
          messageLocaleKey: result
            ? `groupFeed.errors.leave.${result.errorDescription}`
            : 'groupFeed.errors.leave.generic',
          isError: true,
          cardProps: {
            multiLine: true,
          },
        })
      ),
      put(
        groupLeaveFail(
          group,
          (result && result.errorDescription) ||
            i18next.t('groupFeed.errors.leave.generic')
        )
      ),
    ]);
    return;
  }

  yield put(groupLeaveSuccess(group));
  // TODO: have backend return the updated group here as well?
  window.location.reload();
}

export default function* groupsSagaRoot() {
  yield all([
    takeEvery(watchSuccess, handleWatch),
    takeEvery(requestJoinGroup, handleGroupJoinRequest),
    takeEvery(requestLeaveGroup, handleGroupLeaveRequest),
  ]);
}
